<template>
    <CCTable
        :items="tables.multiFieldMapping.items"
        :fields="tables.multiFieldMapping.fields"
        :resizable="false"
    >
        <template #no-items-view>
            <div class="block-no-items section">
                <h2 class="section__title text-no-items">
                    {{ $t("Add the fields to mapping") }}
                </h2>
                <CButton
                    color="success"
                    class="button m-2 shadow"
                    @click="addItemToTable('multiFieldMapping')"
                >
                    <span class="button__icon">
                        <CIcon :content="$options.icons.cilPlus" />
                    </span>
                    <span class="button__text">
                        {{ $t("Add field") }}
                    </span>
                </CButton>
            </div>
        </template>
        <template #under-table>
            <CButton
                color="success"
                class="button m-2 shadow"
                @click="addItemToTable('multiFieldMapping')"
                v-if="tables.multiFieldMapping.items.length > 0"
            >
                <span class="button__icon">
                    <CIcon :content="$options.icons.cilPlus" />
                </span>
                <span class="button__text">
                    {{ $t("Add field") }}
                </span>
            </CButton>
        </template>
        <template #fields="{ index }">
            <SelectAsync
                typeSelect="SelectTags"
                :placeholder="$t('Select a field')"
                size="sm"
                :value.sync="tables.multiFieldMapping.items[index].fields"
                :max="3"
                :options="optionsFields"
                optionsValue="code"
                optionsLabel="name"
            />
        </template>
        <template #name="{ index }">
            <CInput
                :value.sync="tables.multiFieldMapping.items[index].name"
                :placeholder="$t('enter_the_value')"
                :invalidFeedback="$t('value_is_required')"
                class="mb-0"
                size="sm"
            />
        </template>
        <template #options="{ index }">
            <div class="table-options">
                <CButton
                    color="success"
                    variant="outline"
                    square
                    size="sm"
                    @click="toggleDetails('multiFieldMapping', index)"
                >
                    <CIcon class="icon" :content="$options.icons.cilSettings" />
                </CButton>
                <CButton
                    color="danger"
                    variant="outline"
                    square
                    size="sm"
                    @click="deleteItemToTable('multiFieldMapping', index)"
                >
                    <CIcon :content="$options.icons.cilTrash" />
                </CButton>
            </div>
        </template>
        <template #details="{ index, item }">
            <CCollapse :show="Boolean(item._toggled)">
                <CContainer :fluid="true">
                    <CRow class="mb-2">
                        <CCol>
                            <h5>
                                {{ $t("Mapping Values") }}
                            </h5>
                            <p class="mb-0">
                                {{
                                    $t(
                                        "The Mapping Values operation lets you define conditions based on input values (such as exact text matches or regular expressions), and if all conditions are met, it generates a specific output value. If no condition is met, a default value can be returned."
                                    )
                                }}
                            </p>
                        </CCol>
                    </CRow>
                    <CRow>
                        <CCol>
                            <CCTableAddItems
                                :key="item.fields.join(',')"
                                :items.sync="
                                    tables.multiFieldMapping.items[index]
                                        .mappingValues.values
                                "
                                :resizable="false"
                                :fields="[
                                    {
                                        key: 'inputs',
                                        label: 'inputs',
                                        _defaultValue:
                                            getFieldsAsKeyValueObject(
                                                item.fields
                                            ),
                                    },
                                    {
                                        key: 'output',
                                        label: 'output',
                                        _style: 'vertical-align: middle;',
                                    },
                                ]"
                                hover
                                border
                                striped
                            >
                                <template #over-table>
                                    <CContainer fluid>
                                        <CRow>
                                            <CCol>
                                                <CCSwitch
                                                    :checked.sync="
                                                        tables.multiFieldMapping
                                                            .items[index]
                                                            .mappingValues
                                                            .regexp
                                                    "
                                                    :horizontal="
                                                        defaultSwitchConfig.horizontal
                                                    "
                                                    label="Regexp mapping"
                                                    color="primary"
                                                    shape="pill"
                                                    variant="3d"
                                                    size="sm"
                                                />
                                            </CCol>
                                            <CCol>
                                                <CInput
                                                    :value.sync="
                                                        tables.multiFieldMapping
                                                            .items[index]
                                                            .mappingValues
                                                            .defaultValue
                                                    "
                                                    :horizontal="
                                                        defaultInputConfig.horizontal
                                                    "
                                                    label="Default value"
                                                    :placeholder="
                                                        $t(
                                                            'Enter a default value'
                                                        )
                                                    "
                                                    class="mb-0"
                                                    size="sm"
                                                />
                                            </CCol>
                                        </CRow>
                                    </CContainer>
                                </template>
                                <template #output="{ item }">
                                    <CInput
                                        :value.sync="item.output"
                                        :placeholder="
                                            $t('Enter a value of output')
                                        "
                                        :invalidFeedback="$t('Key is required')"
                                        class="mb-0"
                                        size="sm"
                                    />
                                </template>
                                <template #inputs="{ item }">
                                    <CInput
                                        v-for="input in item.inputs"
                                        :key="input.key"
                                        :value.sync="input.value"
                                        size="sm"
                                        :placeholder="
                                            $t(
                                                'Enter the value to look for in ' +
                                                    input.key
                                            )
                                        "
                                        :label="input.key"
                                    >
                                    </CInput>
                                </template>
                            </CCTableAddItems>
                        </CCol>
                    </CRow>
                </CContainer>
            </CCollapse>
        </template>
    </CCTable>
</template>

<script>
import CCTable from "~/components/CCTable";
import CCTableAddItems from "~/components/CCTableAddItems";
import ButtonAdd from "~/components/ButtonAdd";
import SelectAsync from "£lement/SelectAsync";
import SelectTags from "£lement/SelectTags";
import CCSwitch from "~/components/CCSwitch.vue";
import tableMixin from "~/mixins/table.js";
import baseMixin from "~/mixins/base.js";

import {
    cilSave,
    cilArrowThickFromRight,
    cilSettings,
    cilPencil,
    cilTrash,
    cilPlus,
} from "@coreui/icons";
import SelectFields from "£lement/SelectFields";
import { CCol, CContainer, CRow } from "@coreui/vue";

/** Others */
import { mapGetters } from "vuex";

export default {
    name: "CCMultiFieldMapping",
    mixins: [baseMixin, tableMixin],
    icons: {
        cilSave,
        cilArrowThickFromRight,
        cilSettings,
        cilPencil,
        cilTrash,
        cilPlus,
    },
    consts: {},
    options: {},
    components: {
        CCTable,
        CCTableAddItems,
        CCSwitch,
        SelectAsync,
        ButtonAdd,
        SelectTags,
        SelectFields,
    },
    data() {
        return {
            optionsFields: [],
            tables: {
                multiFieldMapping: {
                    fields: [
                        {
                            key: "fields",
                            label: "fields",
                            _defaultValue: [],
                        },
                        {
                            key: "name",
                            label: "Name",
                        },
                        {
                            key: "mappingValues",
                            label: "Mapping Values",
                            _defaultValue: {
                                regexp: false,
                                defaultValue: null,
                                values: [],
                            },
                            _active: false,
                            active: false,
                        },
                        {
                            key: "options",
                            label: "Options",
                        },
                    ],
                    items: [],
                },
            },
        };
    },
    computed: {
        ...mapGetters({
            storeGlobalFieldsAdvance: "global/fieldsAdvance",
        }),
    },
    props: {
        value: {
            required: true,
        },
    },
    watch: {
        value: {
            async handler(newValue) {
                if (newValue) {
                    console.log("Enter here");
                    this.tables.multiFieldMapping.items = newValue;
                }
            },
            deep: true,
        },
        "tables.multiFieldMapping.items": {
            handler(newValue) {
                this.$emit("update:value", newValue);
                this.$emit("change", newValue);
            },
            deep: true,
        },
    },
    methods: {
        getFieldsAsKeyValueObject(fields) {
            return fields.map((field) => {
                return {
                    key: field,
                    value: null,
                };
            });
        },
    },
    async mounted() {
        if (this.value) {
            this.tables.multiFieldMapping.items = this.value;
        }
    },
    async created() {
        if (this.storeGlobalFieldsAdvance.length == 0) {
            await this.loadFieldsAdvance();
        }
        this.optionsFields = this.storeGlobalFieldsAdvance.filter(
            (field) =>
                this.listToArray(field.uses).includes("DELIVERY") ||
                this.listToArray(field.uses).includes("ALL")
        );
    },
};
</script>

<style>
.range__not_defined {
    accent-color: gray;
}
</style>
